import { useMemo } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';

import { useSignatureIntentTrackingData } from './intentData';
import { isSampled } from './shared/sampled';

export type SignatureIntentTrackingResult = Readonly<{
	trackSignIntent: (formData: FormValues) => void;
	trackSignIntentContinue: (formData: FormValues) => void;
	trackSignIntentValid: (formData: FormValues) => void;
	trackPetitionActionsFlow: (petitionActionsEnabled: boolean) => void;
}>;

export function useSignatureIntentTracking(): SignatureIntentTrackingResult {
	const track = useTracking();
	const {
		data: {
			featureConfigs: { signValidationIntentValidTrackSampleRate },
		},
	} = usePetitionDetailsPageData();
	const getTrackingData = useSignatureIntentTrackingData();

	return useMemo(
		() => ({
			trackSignIntent: (formData: FormValues) => {
				void track('sign_intent', getTrackingData(formData));
			},
			trackSignIntentContinue: (formData: FormValues) => {
				void track('sign_intent_continue', getTrackingData(formData));
			},
			trackSignIntentValid: (formData: FormValues) => {
				if (!isSampled(signValidationIntentValidTrackSampleRate)) return;

				void track('sign_intent_valid', getTrackingData(formData));
			},
			trackPetitionActionsFlow: (petitionActionsEnabled: boolean) => {
				void track('petition_actions_flow', { enabled: petitionActionsEnabled });
			},
		}),
		[track, signValidationIntentValidTrackSampleRate, getTrackingData],
	);
}
